import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useDesignStore from '../../store/designStore';
import useProjectStore from '../../store/projectStore';
import useAuthStore from '../../store/authStore';
import useUIStore from '../../store/uiStore';
import ScrollX from '../../components/ScrollX';

const FITTING_TYPES = [
  { value: 'elbow_90_standard', label: '90° Elbow (standard)' },
  { value: 'elbow_90_long_radius', label: '90° Elbow (long radius)' },
  { value: 'elbow_45', label: '45° Elbow' },
  { value: 'tee_through_run', label: 'Tee (through run)' },
  { value: 'tee_branch_flow', label: 'Tee (branch flow)' },
  { value: 'gate_valve_full_open', label: 'Gate Valve (full open)' },
  { value: 'globe_valve_full_open', label: 'Globe Valve (full open)' },
  { value: 'check_valve_swing', label: 'Check Valve (swing)' },
  { value: 'ball_valve_full_open', label: 'Ball Valve (full open)' },
  { value: 'diaphragm_valve', label: 'Diaphragm Valve (weir type, full open)' },
  { value: 'diaphragm_valve_zero_dead_leg', label: 'Diaphragm Valve - Zero Dead Leg (loop, estimate)' },
  { value: 'entrance_sharp', label: 'Pipe Entrance (sharp)' },
  { value: 'exit', label: 'Pipe Exit' },
];

const initialForm = {
  project_id: '',
  flow_rate_m3h: '',
  pipe_internal_diameter_mm: '',
  pipe_length_m: '',
  fluid_density_kgm3: '997',
  fluid_viscosity_cp: '0.89',
  pipe_roughness_um: '0.4',
  elevation_change_m: '0',
  required_discharge_pressure_bar: '',
};

const PipePressureDropCalculatorPage = () => {
  const [form, setForm] = useState(initialForm);
  const [fittings, setFittings] = useState([]);
  const [showDetails, setShowDetails] = useState(true);
  const { projects, fetchProjects } = useProjectStore();
  const { user } = useAuthStore();
  const { result, isCalculating, isSubmitting, error, calculate, saveCalculation, clearResult } = useDesignStore();
  const showToast = useUIStore((s) => s.showToast);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
    clearResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) showToast(error, 'error');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const addFitting = () => {
    setFittings((f) => [...f, { type: FITTING_TYPES[0].value, quantity: 1 }]);
  };

  const updateFitting = (index, field, value) => {
    setFittings((f) => f.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const removeFitting = (index) => {
    setFittings((f) => f.filter((_, i) => i !== index));
  };

  const buildInputs = () => {
    if (!form.flow_rate_m3h || !form.pipe_internal_diameter_mm || !form.pipe_length_m) {
      showToast('Flow rate, pipe diameter, and pipe length are required', 'error');
      return null;
    }
    return {
      flow_rate_m3h: Number(form.flow_rate_m3h),
      pipe_internal_diameter_mm: Number(form.pipe_internal_diameter_mm),
      pipe_length_m: Number(form.pipe_length_m),
      fluid_density_kgm3: Number(form.fluid_density_kgm3),
      fluid_viscosity_cp: Number(form.fluid_viscosity_cp),
      pipe_roughness_um: Number(form.pipe_roughness_um),
      elevation_change_m: Number(form.elevation_change_m || 0),
      required_discharge_pressure_bar:
        form.required_discharge_pressure_bar === '' ? null : Number(form.required_discharge_pressure_bar),
      fittings: fittings.map((f) => ({ type: f.type, quantity: Number(f.quantity) || 1 })),
    };
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    const inputs = buildInputs();
    if (!inputs) return;
    await calculate('pipe_pressure_drop', inputs);
  };

  const handleSave = async () => {
    if (!result) return;
    const payload = {
      calculator_type: 'pipe_pressure_drop',
      project_id: form.project_id ? Number(form.project_id) : null,
      created_by_id: user?.id || null,
      client_data: result.inputs,
      calculation_outputs: result.outputs,
    };
    const saveResult = await saveCalculation(payload);
    if (saveResult.success) {
      showToast('Calculation saved as draft', 'success');
      navigate('/design/calculations');
    } else {
      showToast(saveResult.error, 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-xl font-bold text-white mb-1">Pipe Pressure Drop</h1>
        <p className="text-xs text-gray-500 mb-6">
          Darcy-Weisbach method, Swamee-Jain friction factor. Standard/textbook formulas -
          still needs engineer review before a result is trusted for a build.
        </p>

        <div className="glass-card p-6">
          <form onSubmit={handleCalculate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project (optional)</label>
              <select
                value={form.project_id}
                onChange={handleChange('project_id')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">Not linked to a project</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Flow Rate (m³/hr) *</label>
                <input
                  required
                  type="number"
                  step="any"
                  value={form.flow_rate_m3h}
                  onChange={handleChange('flow_rate_m3h')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Internal Diameter (mm) *</label>
                <input
                  required
                  type="number"
                  step="any"
                  value={form.pipe_internal_diameter_mm}
                  onChange={handleChange('pipe_internal_diameter_mm')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pipe Length (m) *</label>
                <input
                  required
                  type="number"
                  step="any"
                  value={form.pipe_length_m}
                  onChange={handleChange('pipe_length_m')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fluid Density (kg/m³)</label>
                <input
                  type="number"
                  step="any"
                  value={form.fluid_density_kgm3}
                  onChange={handleChange('fluid_density_kgm3')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  placeholder="Default: water @ 25°C"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Viscosity (cP)</label>
                <input
                  type="number"
                  step="any"
                  value={form.fluid_viscosity_cp}
                  onChange={handleChange('fluid_viscosity_cp')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  placeholder="Default: water @ 25°C"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Roughness (µm)</label>
                <input
                  type="number"
                  step="any"
                  value={form.pipe_roughness_um}
                  onChange={handleChange('pipe_roughness_um')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  placeholder="Default: 0.4µm, electropolished SS"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Elevation Change (m)</label>
                <input
                  type="number"
                  step="any"
                  value={form.elevation_change_m}
                  onChange={handleChange('elevation_change_m')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  placeholder="Positive = discharge point is higher"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Required Discharge Pressure (bar)</label>
                <input
                  type="number"
                  step="any"
                  value={form.required_discharge_pressure_bar}
                  onChange={handleChange('required_discharge_pressure_bar')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  placeholder="Optional - for pump/TDH sizing"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Fittings</label>
                <button
                  type="button"
                  onClick={addFitting}
                  className="text-xs font-semibold text-komal-blue hover:underline"
                >
                  + Add Fitting
                </button>
              </div>
              {fittings.length === 0 ? (
                <p className="text-xs text-gray-400">No fittings added.</p>
              ) : (
                <div className="space-y-2">
                  {fittings.map((fitting, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <select
                        value={fitting.type}
                        onChange={(e) => updateFitting(index, 'type', e.target.value)}
                        className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
                      >
                        {FITTING_TYPES.map((ft) => (
                          <option key={ft.value} value={ft.value}>{ft.label}</option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min="1"
                        value={fitting.quantity}
                        onChange={(e) => updateFitting(index, 'quantity', e.target.value)}
                        className="w-20 border border-gray-300 rounded px-3 py-2 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => removeFitting(index)}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="submit"
                disabled={isCalculating}
                className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60"
              >
                {isCalculating ? 'Calculating...' : 'Calculate'}
              </button>
            </div>
          </form>
        </div>

        {result && (
          <div className="glass-card p-6 mt-4">
            <h2 className="font-semibold text-gray-900 mb-4">Result</h2>

            {result.outputs.warnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4 space-y-1">
                {result.outputs.warnings.map((w, i) => (
                  <p key={i} className="text-xs text-amber-800">⚠ {w}</p>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              <Stat label="Velocity" value={`${result.outputs.velocity_m_s} m/s`} />
              <Stat label="Reynolds Number" value={result.outputs.reynolds_number} />
              <Stat label="Flow Regime" value={result.outputs.flow_regime} />
              <Stat label="Friction Factor" value={result.outputs.friction_factor} />
              <Stat label="Friction Head Loss" value={`${result.outputs.friction_head_loss_m} m`} />
              <Stat label="Total Dynamic Head" value={`${result.outputs.total_dynamic_head_m} m`} />
              <Stat label="Total Pressure Required" value={`${result.outputs.total_pressure_required_bar} bar`} />
              <Stat
                label={`Final Pressure Required (${Math.round(result.outputs.safety_margin_fraction * 100)}% margin)`}
                value={`${result.outputs.final_pressure_required_bar} bar`}
                highlight
              />
            </div>

            {(result.outputs.calculation_steps || result.outputs.assumptions) && (
              <div className="mt-6 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowDetails((v) => !v)}
                  className="text-xs font-semibold text-komal-blue hover:underline mb-3"
                >
                  {showDetails ? '− Hide calculation & assumptions' : '+ Show calculation & assumptions'}
                </button>

                {showDetails && (
                  <div className="space-y-4">
                    {result.outputs.calculation_steps?.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                          Calculation Steps
                        </h3>
                        <div className="bg-gray-50 rounded border border-gray-100">
                          <ScrollX>
                          <table className="w-full text-xs">
                            <tbody>
                              {result.outputs.calculation_steps.map((step, i) => (
                                <tr key={i} className="border-b border-gray-100 last:border-0">
                                  <td className="px-3 py-2 font-medium text-gray-700 whitespace-nowrap">{step.step}</td>
                                  <td className="px-3 py-2 text-gray-500 font-mono whitespace-nowrap">{step.formula}</td>
                                  <td className="px-3 py-2 text-gray-900 font-mono">{step.result}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </ScrollX>
                        </div>
                      </div>
                    )}

                    {result.outputs.assumptions?.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                          Assumptions
                        </h3>
                        <ul className="list-disc list-inside space-y-1">
                          {result.outputs.assumptions.map((a, i) => (
                            <li key={i} className="text-xs text-gray-600">{a}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-6">
              <button
                type="button"
                onClick={() => clearResult()}
                className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm rounded bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-60"
              >
                {isSubmitting ? 'Saving...' : 'Save as Draft'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, highlight }) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`font-semibold ${highlight ? 'text-komal-blue text-lg' : 'text-gray-900'}`}>{value}</div>
  </div>
);

export default PipePressureDropCalculatorPage;
